import { RefObject, useEffect, useRef } from 'react';
import { GridApi } from 'ag-grid-community';
import { UseControlledAGGridProps } from '../types';
import { areSetsEqual } from '../utils';

export const useHighlightable = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi | null
): { highlightedRowIdsRef: RefObject<Set<string>> } => {
  const highlightable = props.highlightable;
  const highlightedRowIdsRef = useRef<Set<string>>(new Set());
  const previousRowIdsRef = useRef<Set<string>>(new Set());

  // Keep the ref updated with the latest highlighted row IDs
  useEffect(() => {
    highlightedRowIdsRef.current = highlightable
      ? new Set(highlightable.rowIds)
      : new Set();
  }, [highlightable]);

  // Force AG Grid to re-evaluate row classes when highlighted IDs change
  useEffect(() => {
    if (!api || !highlightable) {
      return;
    }

    const currentRowIds = new Set<string>(highlightable.rowIds);

    // Only redraw if the highlighted row IDs have actually changed
    if (areSetsEqual(previousRowIdsRef.current, currentRowIds)) {
      return;
    }

    previousRowIdsRef.current = currentRowIds;
    // Redraw all rows to apply the updated row classes
    api.redrawRows();
  }, [api, highlightable]);

  return { highlightedRowIdsRef };
};
